/* eslint-disable 
no-param-reassign, 
@typescript-eslint/no-use-before-define 
*/
import { useState, useCallback, useRef, useMemo } from 'react';
import { ObjectSchema, SchemaDescription } from 'yup';

import validate from './validate';
import FormValueTypes from '../types/FormValueTypes';

export type DataSourceProvider = <
  T extends FormValueTypes,
  V extends FormValueTypes = T
>() => FormHookResult<T, V>;

export type FormHookResult<T = FormValueTypes, V = T> = (
  inputName: string,
) => FormInputProps<T, V>;

export type FormInputProps<T = FormValueTypes, V = T> = {
  name: string;
  error: boolean;
  errorText: string;
  value: T;
  onChange: (newValue: V) => void;
};

export type ErrorBundle = { [key: string]: string };
export type GetInputPropsData = { [key: string]: FormInputProps };

export type UseFormHookOptions = {
  /** If true, errors will only be reported on submit, not on input change */
  deferErrorCheck?: boolean;
  /**
   * Registers an input as a dependency of one or more inputs as follows:
   * {
   *   "inputThatHasDependenciesName": ["dependencyName1", "dependencyName2"]
   * }
   * Normally, inputs only check for real time errors when their own value changes. This
   * is normally enough, but there are some edge cases where input A needs to check for
   * errors when input B changes, and this method allows us to register such dependencies.
   * Example: input A must be less than input B, so if B changes to a value lower than
   * what A had at the moment, it should display an error even if the change was not
   * triggered by it.
   */
  inputDependencies?: { [key: string]: string[] };
};

export type UseFormHookResult = {
  /** A key-value pair containing all form erors */
  errors: { [key: string]: string };
  /** A helper method that lets you extract the required data for an specific input name */
  getInputProps: (inputName: string) => FormInputProps;
  /** Called when there are no errors and a submit petition is requested */
  handleSubmit: (event?: React.SyntheticEvent) => void;
  /** Helper method to force multiple inputs to update their values in one state change */
  updateMultipleInputs: (newInputData: {
    [key: string]: FormValueTypes;
  }) => void;
  /**
   * Will return true if all inputs marked as "required" in the validation schema have a
   * value. Note that the value does not have to conform to the rest of the validation rules
   * for this method to return true.
   */
  providedAllRequired: () => boolean;
  /**
   * Helper method that returns a reference of the `useFormHook` method, downcasted for
   * an specific data set. This simplifies the input connection process.
   */
  dataSource: DataSourceProvider;
  /** restored all values without show errors */
  reset: (fieldNames?: string[]) => void;
  /** Update whe current validation schema (dynamic validation) */
  handleUpdateSchema: (schema: ObjectSchema) => void;
  /** returns true if the current content of the form matches the schema passed. Even on the first rendering */
  isValid: boolean;
};

/**
 * Transforms the provider GetInputPropsData value to a key-value pair, compatible
 * with what the `validate` method expects.
 *
 * @param data - Reference to GetInputPropsData instance
 */
function getInputPropsDataToKeyValue(
  data: GetInputPropsData,
): { [key: string]: FormValueTypes } {
  const usedData: { [key: string]: FormValueTypes } = {};
  Object.keys(data).forEach((key: string): void => {
    usedData[key] = data[key].value;
  });
  return usedData;
}

/**
 * Transforms the provided GetInputPropsData value to an ErrorBundle with all the
 * current validation errors.
 *
 * @param data - Reference to GetInputPropsData instance
 */
function getInputPropsDataToErrors(data: GetInputPropsData): ErrorBundle {
  const errors: ErrorBundle = {};
  Object.keys(data).forEach((key: string): void => {
    if (data[key].error && data[key].errorText) {
      errors[key] = data[key].errorText;
    }
  });
  return errors;
}

/**
 * Will return a list of errors based on the provided parameters, which will ignore any
 * existing error generated by an empty field.
 *
 * @param keyValueData - Dictionary containing the current form's values
 * @param errors - The detected errors
 */
function errorsWithoutEmptyCheck(
  keyValueData: { [key: string]: FormValueTypes },
  errors: ErrorBundle,
): ErrorBundle {
  const resultBundle: ErrorBundle = {};
  Object.keys(keyValueData).forEach((key: string): void => {
    if (keyValueData[key] && key in errors) {
      resultBundle[key] = errors[key];
    }
  });
  return resultBundle;
}

/**
 * A hook to manage a form's validation lifecycle
 *
 * @param initialValues - The initial shape of the form's data
 * @param onSubmit - Called when submit is pressed and no validation error occur
 * @param schema - The schema to use for validation
 */
export default function useFormHook<
  T extends { [key: string]: FormValueTypes }
>(
  initialValues: { [key: string]: FormValueTypes },
  onSubmit: (data: T) => void,
  schema: ObjectSchema,
  { deferErrorCheck = false, inputDependencies = {} }: UseFormHookOptions = {},
): UseFormHookResult {
  /** To prevent unnecesarry re-renders, memoize the schema and the dependencies */
  const memoizedSchema = useRef(schema);
  const schemaDescriptionFields = useRef(schema.describe().fields);
  const currentInputDependencies = useRef(inputDependencies).current;
  const memoizeInitialValues = useRef(initialValues);

  /** Initialize */
  const initialInputPropsData: GetInputPropsData = {};
  const initialErrors = useRef(
    errorsWithoutEmptyCheck(
      memoizeInitialValues.current,
      validate(memoizeInitialValues.current, memoizedSchema.current),
    ),
  );
  Object.keys(memoizeInitialValues.current).forEach(
    (valueName: string): void => {
      initialInputPropsData[valueName] = {
        name: valueName,
        value: memoizeInitialValues.current[valueName],
        error: valueName in initialErrors.current,
        errorText:
          valueName in initialErrors.current
            ? initialErrors.current[valueName]
            : '',
        onChange: (newVal: FormValueTypes): void =>
          forceInputChange(getInputPropsData, valueName, newVal),
      };
    },
  );
  const getInputPropsData = useRef(initialInputPropsData).current;

  /**
   * Reference to the internal data, these values will be the ones that forces the
   * re-renders.
   */
  const [errors, updateErrors] = useState<ErrorBundle>(initialErrors.current);
  const [inputData, updateInputData] = useState<{
    [key: string]: FormValueTypes;
  }>(memoizeInitialValues.current);

  /**
   * Returns true if a field is required. Note that a required field must both be
   * defined inside the validation schema and must invoke the `required()` modifier.
   *
   * @param fieldName - The field that will be checked inside the schema
   */
  const fieldIsRequired = useCallback(
    (fieldName: string): boolean => {
      const fieldDescription = schemaDescriptionFields.current[fieldName];
      if (fieldDescription) {
        const [requiredTest] = (fieldDescription as SchemaDescription).tests;
        return requiredTest && requiredTest.name === 'required';
      }
      return false;
    },
    [schemaDescriptionFields.current],
  );

  /**
   * Check if there is an error in any of the inputs specified in inputData,
   * by validating it with the proviced validation schema
   */
  const checkForErrors = (
    data: GetInputPropsData,
  ): { [key: string]: string } => {
    const usedData = getInputPropsDataToKeyValue(data);
    return validate(usedData, memoizedSchema.current);
  };

  /**
   * Assigns the `error` and `errorText` values to the provided input name inside the
   * `source` variable.
   *
   * @param source - The source GetInputPropsData
   * @param errors - The current errors
   * @param inputName - The input to check
   */
  function toggleError(
    source: GetInputPropsData,
    curErrors: ErrorBundle,
    inputName: string,
  ): void {
    if (inputName in curErrors) {
      source[inputName].error = true;
      source[inputName].errorText = curErrors[inputName];
    } else {
      source[inputName].error = false;
      source[inputName].errorText = '';
    }
  }

  /**
   * Checks for errors in a single input, and will trigger check for dependencies
   * if a dependent input changes
   */
  const checkErrorsForSingleInput = (
    data: GetInputPropsData,
    inputName: string,
  ): void => {
    if (deferErrorCheck) {
      toggleError(data, {}, inputName);
      const errorsWithoutProvidedInput = { ...errors };
      delete errorsWithoutProvidedInput[inputName];
      updateErrors(errorsWithoutProvidedInput);
      return;
    }

    const newErrors = checkForErrors(data);

    /** Only validate for the provided input */
    Object.keys(data).forEach((curInputName: string): void => {
      if (curInputName === inputName) {
        toggleError(data, newErrors, curInputName);
      }
    });

    /**
     * Check this input's dependencies
     */
    Object.keys(currentInputDependencies).forEach(
      (sourceDepName: string): void => {
        const dependencyList = currentInputDependencies[sourceDepName];
        if (dependencyList.includes(inputName)) {
          toggleError(data, newErrors, sourceDepName);
          toggleError(data, newErrors, inputName);
        }
      },
    );

    updateErrors(getInputPropsDataToErrors(data));
  };

  /**
   * Returns true if all inputs identified as 'required' have a value.
   * it is useful for disabling the submit button while not all fields are filled, but
   * no errors must be displayed.
   */
  const providedAllRequired = useCallback((): boolean => {
    let completedForm = true;
    Object.keys(memoizeInitialValues.current).forEach(
      (fieldName: string): void => {
        const fieldValue = getInputPropsData[fieldName].value;
        if (fieldIsRequired(fieldName) && fieldValue === '') {
          completedForm = false;
        }
      },
    );
    return completedForm;
  }, [inputData]);

  /**
   * Forces the provided input to change to the newly provided value.
   *
   * @param inputName - The input in which the new value will be forced
   * @param newValue - The new value
   */
  const forceInputChange = (
    data: GetInputPropsData,
    inputName: string,
    newValue: FormValueTypes,
  ): void => {
    data[inputName].value = newValue;
    checkErrorsForSingleInput(data, inputName);
    updateInputData(getInputPropsDataToKeyValue(data));
  };

  /**
   * Restore only the field that we pass in the array
   *
   * @param fieldNames - array with input names to be reset
   */
  const reset = useRef((fieldNames?: string[]): void => {
    const restoreValueBasedOnTheType = (key: string): void => {
      switch (typeof getInputPropsData[key].value) {
        case 'string':
          getInputPropsData[key].value = '';
          break;
        case 'boolean':
          getInputPropsData[key].value = false;
          break;
        case 'number':
          getInputPropsData[key].value = 0;
          break;
        default:
          throw new Error('field type dont supported');
      }
      // delete error state of the field
      getInputPropsData[key].error = false;
      getInputPropsData[key].errorText = '';
    };

    // if we pass a emtpy array or undefined it will restored all value in the form
    if (!fieldNames || fieldNames.length === 0) {
      Object.keys(getInputPropsData).forEach((fieldName) => {
        restoreValueBasedOnTheType(fieldName);
      });
      updateErrors({});
      return;
    }

    // restore only the field specified in fieldNames
    fieldNames.forEach((fieldName) => {
      const errorsWithoutProvidedInput = { ...errors };
      delete errorsWithoutProvidedInput[fieldName];
      restoreValueBasedOnTheType(fieldName);
      updateErrors(errorsWithoutProvidedInput);
    });
  }).current;

  /**
   * When one action needs to update more than 1 input, you need to use this method
   * instead of calling both input's onChange or `forceInputChange`, because they
   * both modify the same state and the result can be unpredictable (most of the times,
   * one of the transactions is dropped)
   *
   * @param newInputData - the new input bundle to update
   */
  const updateMultipleInputs = useRef(
    (newInputData: { [key: string]: FormValueTypes }): void => {
      Object.keys(newInputData).forEach((dataKey: string): void => {
        getInputPropsData[dataKey].value = newInputData[dataKey];
        checkErrorsForSingleInput(getInputPropsData, dataKey);
      });
      updateInputData(newInputData);
    },
  ).current;

  /**
   * Returns all properties related to the provided input name
   *
   * @param inputName - The input name to query
   */
  function getInputProps<
    T extends FormValueTypes,
    V extends FormValueTypes = T
  >(inputName: string): FormInputProps<T, V> {
    return getInputPropsData[inputName] as FormInputProps<T, V>;
  }

  /**
   * Since form inputs have a specific type requirement for their decorators (example: a
   * TextField can only accept string), but the useFormHook doesn't do this discrimination,
   * setting getInputProps directly as the dataSource for most inputs will result in an error.
   * instead, you could use `dataSource` and pass the generic types that the input accepts.
   *
   * Example: for a TextField it would be:
   * <TextField name='fooName' dataSource={dataSource<string>()} />
   *
   * Instead of:
   * <Textfield name='fooName' dataSource={getInputProps as FormHookResult<string>} />
   */
  function dataSource<
    T extends FormValueTypes,
    V extends FormValueTypes = T
  >(): FormHookResult<T, V> {
    return getInputProps as FormHookResult<T, V>;
  }

  /**
   * Calls the submit logic if no validation errors occur. If an error is detected,
   * onSubmit will not be called and the error array will be updated
   *
   * @param event - The submit event. If provided, will auto call the "prevent default" case
   */
  const handleSubmit = (event?: React.SyntheticEvent): void => {
    if (event) {
      event.preventDefault();
    }
    const curInputData = getInputPropsDataToKeyValue(getInputPropsData);
    const newErrors = checkForErrors(getInputPropsData);
    updateErrors(newErrors);
    if (Object.keys(newErrors).length <= 0) {
      onSubmit(curInputData as T);
    } else {
      Object.keys(newErrors).forEach((curInputName: string): void => {
        getInputPropsData[curInputName].error = true;
        getInputPropsData[curInputName].errorText = newErrors[curInputName];
      });
      window.scrollTo(0, 0);
    }
  };

  /**
   * When we have dynamic validation or/and dynamic validation message, update
   * the current schema with the new validation data
   *
   * @param newSchema - Scheme to replace the current one
   */
  const handleUpdateSchema = useRef((newSchema: ObjectSchema) => {
    memoizedSchema.current = newSchema;
    schemaDescriptionFields.current = newSchema.describe().fields;
  }).current;

  const isValid = useMemo(
    () =>
      memoizedSchema.current.isValidSync(
        getInputPropsDataToKeyValue(getInputPropsData),
      ),
    [inputData],
  );

  return {
    errors,
    getInputProps,
    handleSubmit,
    updateMultipleInputs,
    providedAllRequired,
    dataSource,
    reset,
    handleUpdateSchema,
    isValid,
  };
}
